import { useEffect, useMemo } from 'react'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { Bar } from 'react-chartjs-2'

import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  resetReferringPhysicianStatsFilters,
  setReferringPhysicianStatsFilters,
} from '@state/reducers/statsReducer'
import {
  getReferringPhysicianStats,
  getReferringPhysicianStatsOptions,
} from '@state/thunks/statsThunk'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const breakdownChartColor = 'rgba(54, 162, 235, 0.75)'

const getChartHeight = (itemCount: number) => {
  return Math.min(Math.max(itemCount * 36, 280), 720)
}

export default function ReferringPhysicianStats() {
  const dispatch = useAppDispatch()
  const {
    filters,
    sites,
    total,
    departments,
    postalCodes,
    specialties,
    loading,
    loadingOptions,
    error,
    hasLoaded,
    hasLoadedOptions,
  } = useAppSelector(({ stats }) => stats.referringPhysicianStats)

  useEffect(() => {
    if (!hasLoadedOptions) {
      void dispatch(getReferringPhysicianStatsOptions())
    }

    if (!hasLoaded) {
      void dispatch(getReferringPhysicianStats())
    }
  }, [dispatch, hasLoaded, hasLoadedOptions])

  const sortedDepartments = useMemo(
    () => [...departments].sort((left, right) => right.total - left.total),
    [departments],
  )

  const sortedPostalCodes = useMemo(
    () => [...postalCodes].sort((left, right) => right.total - left.total),
    [postalCodes],
  )

  const sortedSpecialties = useMemo(
    () => [...specialties].sort((left, right) => right.total - left.total),
    [specialties],
  )

  const departmentChartData = useMemo(
    () => ({
      labels: sortedDepartments.map(({ departmentCode }) => departmentCode),
      datasets: [
        {
          label: 'Prescripteurs',
          data: sortedDepartments.map(({ total: itemTotal }) => itemTotal),
          backgroundColor: breakdownChartColor,
          borderRadius: 6,
        },
      ],
    }),
    [sortedDepartments],
  )

  const postalCodeChartData = useMemo(
    () => ({
      labels: sortedPostalCodes.map(({ zipCode }) => zipCode),
      datasets: [
        {
          label: 'Prescripteurs',
          data: sortedPostalCodes.map(({ total: itemTotal }) => itemTotal),
          backgroundColor: 'rgba(157, 3, 252, 0.75)',
          borderRadius: 6,
        },
      ],
    }),
    [sortedPostalCodes],
  )

  const specialtyChartData = useMemo(
    () => ({
      labels: sortedSpecialties.map(({ specialty }) => specialty),
      datasets: [
        {
          label: 'Prescripteurs',
          data: sortedSpecialties.map(({ total: itemTotal }) => itemTotal),
          backgroundColor: 'rgba(255, 159, 64, 0.75)',
          borderRadius: 6,
        },
      ],
    }),
    [sortedSpecialties],
  )

  const chartOptions = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            precision: 0,
          },
        },
      },
    }),
    [],
  )

  const handleSiteChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    dispatch(
      setReferringPhysicianStatsFilters({
        siteId: value === '' ? undefined : Number(value),
      }),
    )
  }

  const handleTextFilterChange = (
    key: 'departmentCode' | 'zipCode',
    value: string,
  ) => {
    const normalizedValue = value.trim()

    dispatch(
      setReferringPhysicianStatsFilters({
        [key]: normalizedValue === '' ? undefined : normalizedValue,
      }),
    )
  }

  const handleApplyFilters = () => {
    void dispatch(getReferringPhysicianStats())
  }

  const handleResetFilters = () => {
    dispatch(resetReferringPhysicianStatsFilters())
    void dispatch(getReferringPhysicianStats())
  }

  return (
    <Box sx={{ padding: 3, paddingRight: 2 }}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sx={{
            position: 'sticky',
            top: 16,
            zIndex: 10,
          }}
        >
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h5" sx={{ marginBottom: 2 }}>
                Statistiques des prescripteurs
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="referring-physicians-site-label">Site</InputLabel>
                    <Select
                      labelId="referring-physicians-site-label"
                      value={filters.siteId?.toString() ?? ''}
                      label="Site"
                      onChange={handleSiteChange}
                      disabled={loadingOptions}
                    >
                      <MenuItem value="">Tous les sites</MenuItem>
                      {sites.map((site) => (
                        <MenuItem key={site.id} value={site.id.toString()}>
                          {site.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Département"
                    value={filters.departmentCode ?? ''}
                    onChange={(event) =>
                      handleTextFilterChange(
                        'departmentCode',
                        event.target.value.toUpperCase(),
                      )
                    }
                    placeholder="Ex. 75, 13, 2A"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Code postal"
                    value={filters.zipCode ?? ''}
                    onChange={(event) =>
                      handleTextFilterChange('zipCode', event.target.value)
                    }
                    placeholder="Ex. 75001"
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Button variant="contained" onClick={handleApplyFilters} disabled={loading}>
                    Appliquer
                  </Button>
                  <Button variant="outlined" onClick={handleResetFilters} disabled={loading}>
                    Réinitialiser
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Nombre de prescripteurs</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              <Typography variant="h2">{total}</Typography>
              <Typography variant="body2" color="text.secondary">
                Nombre total de médecins prescripteurs pour les filtres sélectionnés.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Répartition par département</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              {sortedDepartments.length === 0 && !loading ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune donnée trouvée pour les filtres sélectionnés.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: getChartHeight(sortedDepartments.length) }}>
                  <Bar data={departmentChartData} options={chartOptions} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Répartition par code postal</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              {sortedPostalCodes.length === 0 && !loading ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune donnée trouvée pour les filtres sélectionnés.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: getChartHeight(sortedPostalCodes.length) }}>
                  <Bar data={postalCodeChartData} options={chartOptions} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 2,
                }}
              >
                <Typography variant="h5">Répartition par spécialité</Typography>
                {(loading || loadingOptions) && <CircularProgress size={24} />}
              </Box>

              {sortedSpecialties.length === 0 && !loading ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    Aucune donnée trouvée pour les filtres sélectionnés.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: getChartHeight(sortedSpecialties.length) }}>
                  <Bar data={specialtyChartData} options={chartOptions} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
